<template>
  <div class="position-relative">
    <b-form-input
      ref="formulaField"
      class="text-medium"
      :value="value"
      @input="$emit('input', $event)"
      @keyup="checkAutoComplete"
      @keyup.enter="triggerAutoCompleteSelection"
      @keydown.tab="preventFieldBlur"
      @keyup.tab="triggerAutoCompleteSelection"
      @search="triggerAutoCompleteSelection"
      @blur="onBlur"
      @click="checkAutoComplete"
      @keyup.up="selectPreviousItem"
      @keyup.down="selectNextItem"
      @keydown.esc.stop="onBlur"
      @keydown.down.prevent
      @keydown.up.prevent
      id="formula"
      autocomplete="off"
    ></b-form-input>
    <div class="pl-2 pt-1 text-small-1 formula-desc">
      <div v-if="formattedFormula">
        Fields used: <i>{{ formattedFormula }}</i>
      </div>
    </div>
    <div
      class="position-fixed list"
      :style="{
        left: toPixel(formulaMenuRect.left + caretPosition),
        top: toPixel(formulaMenuRect.top)
      }"
      v-if="listItems.length > 0"
    >
      <div
        class="list-item selectable pointer"
        :class="{ selected: index === 0 }"
        :data-id="item.id.toString()"
        v-for="(item, index) in listItems"
        :key="item.id"
        @mousedown="onAutoCompleteSelection(item.id)"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script>
import { KEY_CODES } from '@/constants/keyCodes'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import toPixel from '@/utils/toPixel'
import * as R from 'ramda'
import { CUSTOM_FIELD_TYPE } from '@/constants/customFields'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import { getNQLParser } from '@/nql2'
import { BFormInput } from 'bootstrap-vue'
import getCaretCoordinates from 'textarea-caret'

/**
 * This shows the list the options users can select to build CF formula
 */
export default {
  name: 'CfFormulaAutoCompleteList',
  props: ['orgId', 'customField', 'value', 'projectId'],
  mixins: [ListNavigationMixin],
  components: {
    BFormInput
  },
  data() {
    return {
      autoCompleteMeta: null,
      caretPosition: null,
      formulaMenuRect: {
        top: 0,
        left: 0
      },
      parser: null
    }
  },
  computed: {
    formattedFormula() {
      if (!this.parser || R.isNil(this.value)) return ''
      let symbols = this.findSymbols(this.parser.parse(this.value))
      const cfIdWithName = [...symbols]
        .filter(Boolean)
        .map(cfId => [
          cfId,
          this.$store.getters.customFieldDefinitionById(getIdFromCFPrefixedField(cfId))?.name
        ])
      const fields = cfIdWithName.map(([cfId, name]) => `${cfId} (${name || 'Invalid'})`)
      return fields.join(', ')
    },
    listItems() {
      let options = []
      if (!this.value || !this.autoCompleteMeta) return options
      const searchText = this.value.substring(
        this.autoCompleteMeta.startIndex,
        this.autoCompleteMeta.caretIndex
      )
      if (this.autoCompleteMeta.type === 'symbol') {
        const customFieldOptions = this.availableCustomFields
          .filter(field => field.type === CUSTOM_FIELD_TYPE.NUMBER)
          .map(field => ({
            id: `cf_${field.id}`,
            label: field.name,
            type: field.type
          }))
        options = customFieldOptions
      }
      const searchTerm = searchText?.toLowerCase()
      return (options || []).filter(x => x.label.toLowerCase().startsWith(searchTerm))
    },
    availableCustomFields() {
      if (this.projectId) {
        return this.$store.getters.initiativeEnabledCustomFieldsByProjectId(this.projectId)
      } else {
        return this.$store.getters.customFieldByOrgId(this.orgId)
      }
    }
  },
  methods: {
    toPixel,
    // This function is used by parent component to focus the input field
    focus() {
      this.$refs.formulaField.focus()
    },
    setAutoCompleteMeta(tree, caretIndex) {
      const meta = tree.rootNode.descendantForIndex(caretIndex - 1, caretIndex)
      const isRoot = meta.type === 'source_file'
      const realRoot = meta.children[0]
      this.autoCompleteMeta = {
        type: isRoot ? realRoot.type : meta.type,
        startIndex: meta.startIndex,
        endIndex: meta.endIndex,
        caretIndex
      }
    },
    async checkAutoComplete(event) {
      if (event.keyCode === KEY_CODES.ESCAPE) {
        return
      }
      const { left } = getCaretCoordinates(event.target, event.target.selectionEnd)
      const value = this.value || ''
      const tree = this.parser.parse(value.toLowerCase())
      this.caretPosition = left
      this.setAutoCompleteMeta(tree, event.target.selectionStart)
    },
    triggerAutoCompleteSelection() {
      let selected = this.getSelectedId()
      if (selected) {
        this.onAutoCompleteSelection(selected)
      }
    },
    clearAutoCompleteMeta() {
      this.autoCompleteMeta = null
    },
    onBlur() {
      this.clearAutoCompleteMeta()
    },
    preventFieldBlur(event) {
      let selected = this.getSelectedId()
      if (selected) {
        event.preventDefault()
      }
    },
    onAutoCompleteSelection(selectedOption) {
      let value = this.value
      let [firstHalf, lastHalf] = R.splitAt(this.autoCompleteMeta.startIndex, value)
      lastHalf = lastHalf.substring(
        this.autoCompleteMeta.endIndex - this.autoCompleteMeta.startIndex
      )
      this.$emit('input', `${firstHalf}${selectedOption} ${lastHalf}`)
      let cursorPosition = `${firstHalf}${selectedOption} `.length
      setTimeout(() => {
        this.focus()
        this.$refs.formulaField.setSelectionRange(cursorPosition, cursorPosition)
      }, 100)
      this.clearAutoCompleteMeta()
    },
    calculateFormulaFieldRect() {
      const { top, height, left } = this.$refs.formulaField.$el.getBoundingClientRect()
      this.formulaMenuRect = {
        top: top + height,
        left
      }
    },
    findSymbols(q) {
      if (q.constructor.name === 'Tree') {
        return this.findSymbols(q.rootNode)
      } else if (q.constructor.name === 'Node') {
        if (q.type === 'symbol') {
          return new Set([q.text.toString()]) // Assuming text is a Buffer or ArrayBuffer
        } else {
          let symbols = new Set()
          for (let child of q.children) {
            symbols = new Set([...symbols, ...this.findSymbols(child)]) // Merge sets
          }
          return symbols
        }
      }
    }
  },
  async mounted() {
    window.addEventListener('resize', this.calculateFormulaFieldRect)
    this.calculateFormulaFieldRect()
    this.parser = await getNQLParser()
    setTimeout(this.calculateFormulaFieldRect, 1000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateFormulaFieldRect)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/forms';
@import '~@/styles/_styleConfigurations.scss';

.list {
  top: 45px;
  transition: left ease-in 100ms;
  box-shadow: $menuShadow;
  background: $documentColor;
  z-index: 110;
  border-radius: 0.25rem;
  max-height: 450px;
  overflow: auto;
  min-width: 180px;
  max-width: 350px;
}

.formula-desc {
  min-height: 20px;
}

.list-item {
  padding: 0.25rem 1.5rem;

  &:hover,
  &.selected {
    background-color: $quinary;
  }
}
</style>
